/**
 * Settings — API client.
 * Mirrors src/lib/supabase/settings.ts function signatures exactly.
 */
import { apiGet, apiPost, apiPatch } from './client';

export type { FailureCode, ActionCode, RootCauseCode, RootCauseCategory, OrgSettings } from '@/lib/supabase/settings';
export { DEFAULT_ORG_SETTINGS, ROOT_CAUSE_CATEGORIES, rootCauseCategoryLabels } from '@/lib/supabase/settings';

import type { FailureCode, ActionCode, RootCauseCode, OrgSettings } from '@/lib/supabase/settings';

// ─── Failure Codes ────────────────────────────────────────────────────────────

export async function fetchFailureCodes(_orgId: string | null): Promise<FailureCode[]> {
  return apiGet<FailureCode[]>('/api/settings/failure-codes');
}

export async function createFailureCode(
  code: string,
  label: string,
  category: string,
  _organizationId: string,
): Promise<void> {
  await apiPost<void>('/api/settings/failure-codes', { code, label, category });
}

// ─── Action Codes ─────────────────────────────────────────────────────────────

export async function fetchActionCodes(_orgId: string | null): Promise<ActionCode[]> {
  return apiGet<ActionCode[]>('/api/settings/action-codes');
}

export async function createActionCode(
  code: string,
  label: string,
  _organizationId: string,
): Promise<void> {
  await apiPost<void>('/api/settings/action-codes', { code, label });
}

// ─── Toggle active (shared) ───────────────────────────────────────────────────

export async function toggleCodeActive(
  table: 'failure_codes' | 'action_codes' | 'root_cause_codes',
  id: string,
  active: boolean,
): Promise<void> {
  const endpointMap: Record<string, string> = {
    failure_codes: `/api/settings/failure-codes/${id}`,
    action_codes: `/api/settings/action-codes/${id}`,
    root_cause_codes: `/api/settings/root-cause-codes/${id}`,
  };
  await apiPatch<void>(endpointMap[table], { active });
}

export async function updateFailureCode(
  id: string,
  patch: { code?: string; label?: string; category?: string },
): Promise<void> {
  await apiPatch<void>(`/api/settings/failure-codes/${id}`, patch);
}

export async function updateActionCode(
  id: string,
  patch: { code?: string; label?: string },
): Promise<void> {
  await apiPatch<void>(`/api/settings/action-codes/${id}`, patch);
}

// ─── Root Cause Codes ────────────────────────────────────────────────────────

export async function fetchRootCauseCodes(_orgId: string | null): Promise<RootCauseCode[]> {
  return apiGet<RootCauseCode[]>('/api/settings/root-cause-codes');
}

export async function createRootCauseCode(
  code: string,
  label: string,
  category: string,
  _organizationId: string,
): Promise<void> {
  await apiPost<void>('/api/settings/root-cause-codes', { code, label, category });
}

// ─── Org Settings ─────────────────────────────────────────────────────────────

export async function fetchOrgSettings(_orgId: string): Promise<OrgSettings> {
  return apiGet<OrgSettings>('/api/settings/org');
}

export async function updateOrgSettings(_orgId: string, patch: Partial<OrgSettings>): Promise<void> {
  await apiPatch<void>('/api/settings/org', patch);
}
